import { ApplyToTrialButton } from '@components/app/Button/ApplyToTrial/index.client'
import { ViewTrialsButton } from '@components/app/Button/ViewTrials'
import type { TreatmentPageSection } from '@components/app/TreatmentPage/TableOfContents'
import { Text } from '@components/primitives/Text'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import type { TrialAssociation } from '@modules/trials/types/TrialAssociation'
import { useEffect, useState } from 'react'
import type { InViewHookResponse } from 'react-intersection-observer'

export type ListingSection =
  | 'design'
  | 'effectiveness'
  | 'studyObjectives'
  | 'eligibilityCriteria'
  | 'faq'
  | 'logistics'
  | 'relatedTrials'
  | 'safety'
  | 'summary'

type TrialListingPageRefs = {
  [sectionId in ListingSection]: InViewHookResponse
}

export const TableOfContents = ({
  hasFaqs,
  hasRelatedTrials,
  heroInView,
  sectionRefs,
  trial,
}: {
  hasFaqs: boolean
  hasRelatedTrials: boolean
  heroInView: boolean
  sectionRefs: TrialListingPageRefs
  trial: TrialAssociation
}) => {
  const [btnBlack, setbtnBlack] = useState(false)

  useEffect(() => {
    const entry = sectionRefs.eligibilityCriteria.entry
    if (!entry) return

    if (!heroInView) {
      setbtnBlack(true)
    } else {
      setbtnBlack(false)
    }
  }, [heroInView, sectionRefs.eligibilityCriteria.entry])

  return (
    <nav className='sticky top-24 hidden rounded-2xl border-2 border-neutral100 p-6 lg:block'>
      <div className='mb-4 flex flex-col space-y-1'>
        <TableOfContentsItem
          hook={sectionRefs.summary}
          sectionId='summary'
          text='Summary'
        />
        <TableOfContentsItem
          hook={sectionRefs.effectiveness}
          sectionId='effectiveness'
          text='Effectiveness'
        />
        <TableOfContentsItem
          hook={sectionRefs.studyObjectives}
          sectionId='studyObjectives'
          text='Study Objectives'
        />
        <TableOfContentsItem
          hook={sectionRefs.safety}
          sectionId='safety'
          text='Safety'
        />
        <TableOfContentsItem
          hook={sectionRefs.design}
          sectionId='design'
          text='Trial Design'
        />
        <TableOfContentsItem
          hook={sectionRefs.logistics}
          sectionId='logistics'
          text='Logistics'
        />
        <TableOfContentsItem
          hook={sectionRefs.eligibilityCriteria}
          sectionId='eligibilityCriteria'
          text='Eligibility Criteria'
        />
        {hasFaqs && (
          <TableOfContentsItem
            hook={sectionRefs.faq}
            sectionId='faq'
            text='FAQs'
          />
        )}
      </div>
      <ApplyToTrialButton
        className={`${btnBlack ? '' : 'border-4 border-neutral100'} mt-2`}
        color={btnBlack ? 'black' : 'lightGrey'}
        source={TrackingSources.LISTING_TABLE_OF_CONTENTS}
        trial={trial}
      />
      {hasRelatedTrials && <ViewTrialsButton />}
    </nav>
  )
}

export const TableOfContentsItem = ({
  hook,
  sectionId,
  text,
}: {
  hook: InViewHookResponse
  sectionId: ListingSection | TreatmentPageSection
  text: string
}) => {
  if (!hook.entry) {
    // Don't render if the content is not on the page
    return null
  }
  const color = hook.inView ? 'text-primary500' : 'text-neutral500'

  return (
    <a href={`#${sectionId}`}>
      <Text
        className={`${hook.inView ? '' : 'hover:text-neutral600'} ${color}`}
        styleName='p-strong'
        value={text}
      />
    </a>
  )
}
